// components/About/BioText.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { useStaticQuery, graphql } from 'gatsby';

const BioTextContainer = styled.p`
  font-size: 1.88vw;
  line-height: 1.4;
  margin: 0;
  margin-bottom: 40px;
  font-weight: 400;
  color: #565656;

  @media (max-width: 1200px) {
    font-size: 30px;
  }
  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const NameSpan = styled.span`
  cursor: default;
`;

const LinkContainer = styled.span`
  position: relative;
  display: inline-block;
`;

const StyledLink = styled.a`
  font-weight: bold;
  text-decoration: none;
  border-bottom: 1px solid ${(props) => props.color || 'black'};
  color: ${(props) => props.color || 'black'};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) => props.color || 'black'};
    color: white;
  }
`;

const PreviewImage = styled.div`
  position: fixed;
  width: 192px;
  height: 128px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  pointer-events: none;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
  }
`;

const LinkWithPreview = ({ href, children, imageName, color }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const data = useStaticQuery(graphql`
    query {
      allFile(
        filter: {
          sourceInstanceName: { eq: "images" }
          extension: { in: ["jpg", "jpeg", "png", "webp", "gif"] }
        }
      ) {
        nodes {
          relativePath
          childImageSharp {
            gatsbyImageData(
              width: 192
              height: 128
              placeholder: BLURRED
              layout: FIXED
            )
          }
        }
      }
    }
  `);

  const image = data.allFile.nodes.find(
    (node) => node.relativePath === `thumbnails/${imageName}`
  );

  const gatsbyImageData = image ? getImage(image) : null;

  const handleMouseMove = (e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  return (
    <LinkContainer
      onMouseEnter={() => setShowPreview(true)}
      onMouseLeave={() => setShowPreview(false)}
      onMouseMove={handleMouseMove}
    >
      <StyledLink
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        color={color}
      >
        {children}
      </StyledLink>

      {showPreview && gatsbyImageData && (
        <PreviewImage
          style={{
            left: `${mousePosition.x + 16}px`,
            top: `${mousePosition.y + 16}px`,
          }}
        >
          <GatsbyImage image={gatsbyImageData} alt={`Preview of ${children}`} />
        </PreviewImage>
      )}
    </LinkContainer>
  );
};

const BioText = ({ onNameHover }) => {
  const purpleColor = '#401F68';
  const blackColor = '#000000';

  return (
    <BioTextContainer>
      <NameSpan
        onMouseEnter={() => onNameHover(true)}
        onMouseLeave={() => onNameHover(false)}
      >
        Jesse (Chenfeng) Gao - 高晨峰
      </NameSpan>{' '}
      is a HCI researcher/designer interested in human digitalization,
      Reality-Virtuality Continuum and creative interface. He is currently a 2st
      year PhD student in{' '}
      <LinkWithPreview
        href="https://spice-lab.org/"
        color={purpleColor}
        imageName="spice.png"
      >
        SPICElab
      </LinkWithPreview>{' '}
      at{' '}
      <LinkWithPreview
        href="https://tsb.northwestern.edu/"
        color={purpleColor}
        imageName="northwestern.png"
      >
        Northwestern University
      </LinkWithPreview>
      , advised by{' '}
      <LinkWithPreview
        href="https://karan-ahuja.com/"
        color={purpleColor}
        imageName="karan.png"
      >
        Prof. Karan Ahuja
      </LinkWithPreview>
      . He graduated with an MS in Computer Science from University of Chicago,
      where he worked with{' '}
      <LinkWithPreview
        href="https://www.axlab.cs.uchicago.edu/"
        color={blackColor}
        imageName="ken.png"
      >
        Prof. Ken Nakagaki
      </LinkWithPreview>{' '}
      in AxLab on tangible and actuated interactions research. Prior to that, he
      received his Master of Design in Human-Computer Interaction from{' '}
      <LinkWithPreview
        href="https://id.iit.edu/"
        color={blackColor}
        imageName="id.png"
      >
        Institute of Design
      </LinkWithPreview>{' '}
      (ID), where Prof. Anijo Mathew, Prof. Santosh Basapur, and Prof. Zach Pino
      mentored him in human-centered design. He completed his undergraduate
      studies in Mathematics and Applied Mathematics at Renmin University of
      China.
    </BioTextContainer>
  );
};

export default BioText;
